import React, { useState, useMemo, useEffect } from 'react';
import { Layout, theme } from 'antd';
import { PDFViewer } from '@react-pdf/renderer';
import EnhancedControlPanel from './components/EnhancedControlPanel';
import PDFDocument from './components/PDFDocument';
import './utils/fontRegistry'; // 确保字体在应用启动时注册
import { setGlobalFont } from './utils/fontRegistry';
import './App.css';
import FontLoader from './components/FontLoader';

const { Content, Sider } = Layout;

// 默认文档数据
const defaultDocumentData = {
  title: {
    english: "Why You should Never Retire",
    chinese: "为什么你永远不应该退休"
  },
  header: {
    text: "外刊双语精读 | 为什么你永远不应该退休",
    logoUrl: "" // 空字符串，将使用默认Logo占位图片
  },
  summary: {
    title: "为什么你永远不应该退休",
    source: "选⾃The Economist"
  },
  chineseGuide: {
    title: "中⽂导读",
    content: [
      "退休意味着离开繁忙的⼯作世界，进⼊休闲⽣活",
      "但仍需⾯对收⼊、⽬标和关联性等潜在痛苦损失"
    ]
  },
  backgroundImage: "", // 空字符串，将使用默认背景图片占位
  paragraphs: [
    {
      english: `In an episode of "The Sopranos", a popular television series which started airing in the 1990s, a gangster tells Tony, from the titular family, that he wants to retire. "What are you, a hockey player?" Tony snaps back. Non-fictional non-criminals who are considering an end to their working lives need not worry about broken fingers or other bodily harm. But they must still contend with other potentially painful losses: of income, purpose or, most poignantly, relevance`,
      chinese: "在《黑道家族》的一集中，这部始于1990年代的热门电视剧里，一个黑帮分子告诉托尼，这个同名家族的成员，他想要退休。'你是什么，曲棍球运动员吗？'托尼反驳道。那些考虑结束工作生涯的非虚构非罪犯不必担心手指骨折或其他身体伤害。但他们仍然必须面对其他潜在的痛苦损失：收入、目标，或者最令人痛心的，相关性。",
      words: [
        {
          name: "episode",
          phonetic: "英 /ˈepɪsəʊd/ 美 /ˈepɪsoʊd/",
          partOfSpeech: "n. 插曲，片段；一集",
          example: "I watched the latest episode of the series.",
          scopeWord: "CET4 / CET6 / IELTS / GRE",
          chExample: "我看了这部剧的最新一集。"
        },
        {
          name: "gangster",
          phonetic: "英 /ˈɡæŋstə(r)/ 美 /ˈɡæŋstər/",
          partOfSpeech: "n. 黑帮分子，歹徒",
          example: "The gangster was arrested by the police.",
          scopeWord: "CET4 / CET6 / IELTS / GRE",
          chExample: "那个黑帮分子被警察逮捕了。"
        },
        {
          name: "relevance",
          phonetic: "英 /ˈreləvəns/ 美 /ˈreləvəns/",
          partOfSpeech: "n. 相关性，关联性",
          example: "The relevance of this topic is clear.",
          scopeWord: "CET4 / CET6 / IELTS / GRE",
          chExample: "这个话题的相关性很明显。"
        }
      ]
    }
  ],
  phraseCollection: {
    title: "【短语积累】",
    items: [
      { content: "Marginalise a group - 边缘化一个群体" },
      { content: "Impairment of vision - 视力受损" },
      { content: "Moderately priced - 价格适中的" },
      { content: "Prestige of the university - 大学的声望" },
      { content: "Predictable outcome - 可预测的结果" },
      { content: "Measly salary - 微薄的薪水" }
    ]
  },
  paragraphSummary: {
    title: "【段落⼤意】",
    items: [
      { content: "⼈们考虑结束⼯作⽣涯时，不必担⼼身体伤害，但仍需⾯对收⼊、⽬标和关联性等潜在痛苦损失。" },
      { content: "⼀些⼈坚决不会退休，如乔治·阿玛尼和查理·芒格，他们在⾼龄时仍保持职业活动。" },
      { content: "像阿玛尼、巴菲特和芒格这样的⼈虽然特殊，但在职业上保持活跃并不独⼀⽆⼆，许多美国⼈表示可能永远不会退休。" }
    ]
  }
};

function App() {
  // 文档数据状态
  const [documentData, setDocumentData] = useState(defaultDocumentData);
  // 水印配置状态
  const [watermarkConfig, setWatermarkConfig] = useState({
    enabled: false,
    text: '外刊来了',
    opacity: 0.3,
    rotation: -45,
    fontSize: 12,
    color: '#cccccc',
    density: 'medium',
    layer: 'front' // 'front' 最前层, 'back' 最后层
  });

  // 添加一个强制更新的key，确保PDF组件重新渲染
  const [pdfKey, setPdfKey] = useState(0);
  
  // 添加字体加载状态
  const [fontsLoaded, setFontsLoaded] = useState(false);

  // 强制更新PDF的函数
  const forceUpdatePDF = () => {
    setPdfKey(prev => prev + 1);
  };

  // 处理全局字体变化
  const handleGlobalFontChange = (newFontConfig) => {
    // 更新全局字体配置
    setGlobalFont(newFontConfig.selectedFont);
    // 强制更新PDF以应用新字体
    forceUpdatePDF();
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  // 确保组件初始化时触发PDF渲染
  useEffect(() => {
    // 初始化时强制更新一次PDF，确保默认数据能正确渲染
    if (fontsLoaded) {
      forceUpdatePDF();
    }
  }, [fontsLoaded]); // 只在字体加载完成和组件挂载时执行

  // 处理文档数据变化
  const handleDocumentDataChange = (newData) => {
    setDocumentData(newData);
    forceUpdatePDF(); // 强制更新PDF预览
  };

  // 使用useMemo确保PDF文档在props变化时重新创建
  const pdfDocument = useMemo(() => {
    return (
      <PDFDocument
        key={`pdf-${pdfKey}`}
        documentData={documentData} // 始终传递documentData，PDFDocument内部会处理fallback
        watermarkConfig={watermarkConfig} // 传递水印配置
      />
    );
  }, [documentData, watermarkConfig, pdfKey]);

  // 渲染传统编辑模式
  const renderTraditionalMode = () => (
    <Layout>
      {/* 左侧PDF预览区域 */}
      <Content style={{
        margin: '16px',
        background: colorBgContainer,
        borderRadius: borderRadiusLG,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div style={{
          flex: 1,
          padding: '16px',
          overflow: 'hidden'
        }}>
          <div style={{
            width: '100%',
            height: '100%',
            border: '1px solid #d9d9d9',
            borderRadius: '6px',
            overflow: 'hidden'
          }}>
            <PDFViewer
              key={`iframe-${pdfKey}`}
              style={{
                width: '100%',
                height: '100%',
                border: 'none'
              }}
              showToolbar={true}
            >
              {pdfDocument}
            </PDFViewer>
          </div>
        </div>
      </Content>

      {/* 右侧控制面板 */}
      <Sider
        width={400}
        style={{
          background: colorBgContainer,
          margin: '16px 16px 16px 0',
          borderRadius: borderRadiusLG,
          overflow: 'hidden'
        }}
      >
        <EnhancedControlPanel
          documentData={documentData}
          onDocumentDataChange={handleDocumentDataChange}
          onGlobalFontChange={handleGlobalFontChange}
          watermarkConfig={watermarkConfig}
          setWatermarkConfig={setWatermarkConfig}
        />
      </Sider>
    </Layout>
  );

  // 字体加载完成回调
  const handleFontsLoaded = () => {
    console.log('字体加载完成');
    setFontsLoaded(true);
  };

  return (
    <Layout style={{ height: '100vh' }}>
      {!fontsLoaded ? (
        <FontLoader onComplete={handleFontsLoaded} />
      ) : (
        renderTraditionalMode()
      )}
    </Layout>
  );
}

export default App;
